import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Calendar, Clock, AlertCircle, FileText, UserMinus, Plus, Loader2, History } from "lucide-react";
import api from "../utils/api";
import { toast } from "sonner";
import { toSydneyDate } from "../utils/helpers";

interface AttendanceTabProps {
  userId: string;
}

export const AttendanceTab: React.FC<AttendanceTabProps> = ({ userId }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const res = await api.get(`/api/attendance/user/${userId}`);
        if (res.success) {
          setData(res);
        }
      } catch (error) {
        console.error("Error fetching attendance data:", error);
        toast.error("Failed to load attendance data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const { balances, history } = data;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Approved":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-100 font-bold">Approved</Badge>;
      case "Reported":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-100 font-bold">Reported</Badge>;
      default:
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-100 font-bold">{status}</Badge>;
    }
  };

  const formatDate = (dateStr: string) => {
    return toSydneyDate(dateStr);
  };

  return (
    <div className="space-y-6 mt-6">

      {/* Leave Balances */}
      <div className="grid gap-4 md:grid-cols-3">
        {Object.entries(balances).map(([type, balance]: [string, any]) => (
          <Card key={type} className="border-none shadow-sm bg-gray-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-gray-500 uppercase tracking-wider">{type}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {balance.remaining.toFixed(4)} <span className="text-xs font-medium text-gray-500">Hours Remaining</span>
              </div>
              <div className="flex flex-col mt-2 gap-1">
                <div className="flex justify-between text-[10px] font-bold text-gray-400">
                  <span>USED</span>
                  <span>{balance.used.toFixed(4)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Attendance History */}
      <Card className="border-none shadow-sm overflow-hidden">
        <CardHeader className="bg-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-500" />
              <CardTitle className="text-lg font-bold">Recent History</CardTitle>
            </div>
            <p className="text-xs text-gray-400 font-medium italic">Showing last 20 events</p>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-50/50">
                <TableRow className="border-gray-100">
                  <TableHead className="font-bold text-gray-900 uppercase text-[10px] tracking-wider">Date</TableHead>
                  <TableHead className="font-bold text-gray-900 uppercase text-[10px] tracking-wider">Event</TableHead>
                  <TableHead className="font-bold text-gray-900 uppercase text-[10px] tracking-wider">Type</TableHead>
                  <TableHead className="font-bold text-gray-900 uppercase text-[10px] tracking-wider">Duration</TableHead>
                  <TableHead className="font-bold text-gray-900 uppercase text-[10px] tracking-wider">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-400 italic font-medium">No attendance records found for this year.</TableCell>
                  </TableRow>
                ) : (
                  history.map((event: any) => (
                    <TableRow key={event.id} className="border-gray-100 hover:bg-gray-50/50 transition-colors">
                      <TableCell className="font-bold text-gray-900 whitespace-nowrap text-xs">
                        {event.endDate && event.endDate !== event.date ? (
                          `${formatDate(event.date)} - ${formatDate(event.endDate)}`
                        ) : (
                          formatDate(event.date)
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {event.event === 'Leave' ? (
                            <FileText className="h-3 w-3 text-blue-500" />
                          ) : (
                            <AlertCircle className="h-3 w-3 text-red-500" />
                          )}
                          <span className="font-bold text-gray-700 text-xs">{event.event}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-gray-600 text-xs">{event.type}</TableCell>
                      <TableCell className="font-bold text-gray-900 whitespace-nowrap text-xs">
                        {Number(event.hours || 0).toFixed(2)} hours
                      </TableCell>
                      <TableCell>{getStatusBadge(event.status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
